import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Globe,
  MapPin,
  Trophy,
  Plus,
  LogOut,
  User,
  ChevronRight,
  Edit,
  Trash2,
  Save
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const ToursPage = () => {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', region: '' });

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_URL}/tours`);
      setTours(response.data);
    } catch (error) {
      toast.error('Failed to load tours');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a tour name');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/tours?admin_id=${member.id}`, {
        name: form.name,
        description: form.description,
        region: form.region
      });
      toast.success('Tour created successfully');
      setCreating(false);
      setForm({ name: '', description: '', region: '' });
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a tour name');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/tours/${editingTour.id}?admin_id=${member.id}`, {
        name: form.name,
        description: form.description,
        region: form.region
      });
      toast.success('Tour updated successfully');
      setEditingTour(null);
      setForm({ name: '', description: '', region: '' });
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tourId, tourName) => {
    if (!window.confirm(`Delete "${tourName}"? Tournaments will be unassigned but not deleted.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/tours/${tourId}?admin_id=${member.id}`);
      toast.success('Tour deleted');
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tour');
    }
  };

  const openEdit = (tour) => {
    setForm({ name: tour.name, description: tour.description || '', region: tour.region || '' });
    setEditingTour(tour);
  };

  const openCreate = () => {
    setForm({ name: '', description: '', region: '' });
    setCreating(true);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img
              src={TGA_LOGO}
              alt="TGA Pro Tour"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-logo"
            />
            <div className="hidden sm:flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/schedule')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-schedule"
              >
                Schedule
              </Button>
              <Button
                variant="ghost"
                className="text-white text-sm border-b-2 border-primary rounded-none"
                data-testid="nav-tours-active"
              >
                Tours
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/rankings')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-rankings"
              >
                Rankings
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {member?.is_admin && (
              <span className="px-2 py-1 rounded bg-primary/20 text-primary text-xs font-medium border border-primary/50">
                Admin
              </span>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-muted-foreground hover:text-white"
              data-testid="nav-profile-btn"
            >
              <User className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{member?.name}</span>
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="nav-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Globe className="w-8 h-8 text-primary" />
              <h1 className="text-3xl sm:text-4xl font-heading text-white">
                Pro Tours
              </h1>
            </div>
            <p className="text-muted-foreground">
              Browse tournament tours across the nation
            </p>
          </div>
          {member?.is_admin && (
            <Dialog open={creating} onOpenChange={setCreating}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreate}
                  className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale glow-purple"
                  data-testid="create-tour-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Tour
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create New Tour</DialogTitle>
                  <DialogDescription>Set up a new pro tour to group tournaments</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 my-4">
                  <div className="space-y-2">
                    <Label>Tour Name</Label>
                    <Input
                      placeholder="e.g., South Florida Pro Tour"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-muted border-border"
                      data-testid="tour-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Region / Location</Label>
                    <Input
                      placeholder="e.g., South Florida"
                      value={form.region}
                      onChange={(e) => setForm({ ...form, region: e.target.value })}
                      className="bg-muted border-border"
                      data-testid="tour-region-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe this tour..."
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="bg-muted border-border min-h-[80px]"
                      data-testid="tour-description-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    onClick={handleCreate}
                    disabled={saving}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="tour-save-btn"
                  >
                    {saving ? 'Creating...' : 'Create Tour'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Edit Tour Dialog */}
        <Dialog open={!!editingTour} onOpenChange={(open) => !open && setEditingTour(null)}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Tour</DialogTitle>
              <DialogDescription>Update tour details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-4">
              <div className="space-y-2">
                <Label>Tour Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="edit-tour-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Region / Location</Label>
                <Input
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="edit-tour-region-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-muted border-border min-h-[80px]"
                  data-testid="edit-tour-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                onClick={handleUpdate}
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
                data-testid="edit-tour-save-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tours List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-primary">Loading tours...</div>
          </div>
        ) : tours.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="p-12 text-center">
              <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">No Tours Yet</h3>
              <p className="text-muted-foreground mb-6">
                {member?.is_admin
                  ? 'Create your first pro tour to start grouping tournaments.'
                  : 'No tours have been created yet. Check back soon!'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {tours.map((tour, index) => (
              <Card
                key={tour.id}
                className="bg-card border-white/10 tournament-card cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/tours/${tour.id}`)}
                data-testid={`tour-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-heading text-white">
                          {tour.name}
                        </h3>
                      </div>
                      {tour.description && (
                        <p className="text-muted-foreground text-sm mb-3">
                          {tour.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {tour.region && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            {tour.region}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-primary font-mono">
                          <Trophy className="w-4 h-4" />
                          {tour.tournament_count} Tournament{tour.tournament_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member?.is_admin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openEdit(tour); }}
                            className="border-primary/50 hover:bg-primary/20"
                            data-testid={`edit-tour-${index}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDelete(tour.id, tour.name); }}
                            className="border-destructive/50 hover:bg-destructive/20 text-destructive"
                            data-testid={`delete-tour-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <ChevronRight className="w-6 h-6 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
